import React from "react";
import { colors } from "../../theme";
import { useStudio } from "../../store/useStudio";
import { PILLAR_BY_ID } from "../../data/matrices";
import { circularityLoops, circularityScore, systemsFor, cropGroups, resourcesFor, effectiveCircWeights, CIRC_PRESETS } from "../../lib/kbLogic";
import { systemMinFootprint } from "../../lib/anchorRipple";
import { RING_GUIDE, getOptTip } from "../../data/ringGuide";
import { kbApi } from "../../api/kb";
import { warn } from "../../lib/log";
import { styles, RING_COLOR, MINT } from "./tokens";
import { cropIcon, systemIcon, resourceIcon, locationIcon } from "./icons";
import { GAP_SUGGEST, resourceGroup } from "./helpers";
import { KbStat, OptionRow, ReferencesButton, Hint } from "./primitives";
import { PairedInstances, SystemBlocksPanel } from "./blocks";
import { RippleBanner, PairPanel, CropPairPanel, PausedDrafts, DraftPrompt, systemDisplayLabel, sortSystemsByHost } from "./drafting";
import SystemSelect from "./SystemSelect";
import { CircularityDetailModal } from "./CircularityDetail";
import { LOOP_FORMULA } from "./CircularityDetail";
import { Search, SlidersHorizontal } from "lucide-react";
import { ResourceOption } from "./resources";
import { InfoTip } from "../InfoTip";

// Lightweight guide encoding the intended build order across every gate:
// Zone (optional) → System (required, the brains) → Crops (optional).
const FLOW_STEPS = [
  { key: "locations", n: 1, label: "Zone", opt: "optional", color: PILLAR_BY_ID.locations.solid,
    tip: "A 2D allocation area on the model: draw it, enter an area, or select a volume. One zone can host multiple systems and reserve circulation or service space. If omitted, each system's own footprint defines its zone." },
  { key: "systems", n: 2, label: "System", opt: "required", color: PILLAR_BY_ID.systems.solid,
    tip: "The core of each scenario. The selected growing system sets the compatible area, crops and resource loops." },
  { key: "crops", n: 3, label: "Crop", opt: "", color: PILLAR_BY_ID.crops.solid,
    tip: "Assign crops to a system to compute yield, DLI suitability and market value." },
  { key: "resources", n: 4, label: "Resources", opt: "", color: PILLAR_BY_ID.resources.solid,
    tip: "Recovery and generation loops (water, energy, thermal and organics) that offset the design's demand." },
];

export function FlowStepper({ active }) {
  const setRoot = useStudio((s) => s.setRoot);
  return (
    <div style={fstyles.wrap} data-testid="flow-stepper">
      {FLOW_STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <button type="button" onClick={() => setRoot(s.key)} title={`Go to ${s.label}`} style={{ ...fstyles.step, ...(active === s.key ? { borderColor: s.color, background: `${s.color}14` } : {}) }} data-testid={`flow-step-${s.key}`}>
            <span style={{ ...fstyles.num, background: active === s.key ? s.color : "#c3ccd6" }}>{s.n}</span>
            <span style={fstyles.label}>{s.label}</span>
            {s.opt && <span style={fstyles.opt}>{s.opt}</span>}
            <InfoTip testId={`flow-${s.key}-info`} title={s.opt ? `${s.label} · ${s.opt}` : s.label} body={s.tip} placement="bottom" />
          </button>
          {i < FLOW_STEPS.length - 1 && <span style={fstyles.arrow}>→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

const fstyles = {
  wrap: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", padding: "8px 10px", marginBottom: 10, borderRadius: 12, background: "#f5f7f9", border: `1px solid ${colors.border}` },
  step: { display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 999, borderWidth: 1.5, borderStyle: "solid", borderColor: "transparent", background: "transparent", cursor: "pointer", fontFamily: "inherit" },
  num: { width: 16, height: 16, borderRadius: "50%", color: "#fff", fontSize: 9.5, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  label: { fontSize: 11.5, fontWeight: 700, color: colors.textPrimary },
  opt: { fontSize: 9, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase", color: colors.textTertiary },
  arrow: { color: colors.textTertiary, fontSize: 12, fontWeight: 700 },
};

export function CircularityGaps({ kpis }) {
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const kb = useStudio((s) => s.kb);
  const selections = useStudio((s) => s.selections);
  const loopHistory = useStudio((s) => s.loopHistory);
  const recordLoopSnapshot = useStudio((s) => s.recordLoopSnapshot);
  const circWeights = useStudio((s) => s.circWeights);
  const setCircWeight = useStudio((s) => s.setCircWeight);
  const resetCircWeights = useStudio((s) => s.resetCircWeights);
  const circLockEqual = useStudio((s) => s.circLockEqual);
  const setCircLockEqual = useStudio((s) => s.setCircLockEqual);
  const applyCircPreset = useStudio((s) => s.applyCircPreset);
  const valorizationAreaCharged = useStudio((s) => s.valorizationAreaCharged);
  const setValorizationAreaCharged = useStudio((s) => s.setValorizationAreaCharged);
  const [detailKey, setDetailKey] = React.useState(null);
  const [showWeights, setShowWeights] = React.useState(false);
  const bal = kpis?.balance;
  const eff = effectiveCircWeights(circWeights, circLockEqual);
  const rows = bal ? circularityLoops(bal, eff) : [];
  // Record one trend snapshot per committed-system count (real computed pcts only).
  const sysCount = systemBlocks?.length || 0;
  React.useEffect(() => {
    if (!bal || !rows.length) return;
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r.pct]));
    recordLoopSnapshot({ systems: sysCount, water: byKey.water ?? null, energy: byKey.energy ?? null, nutrient: byKey.nutrient ?? null, carbon: byKey.carbon ?? null });
  }, [sysCount]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!bal) return null;
  if (!rows.length) {
    return (
      <div data-testid="circularity-gaps-empty" style={{ padding: "16px 12px", borderRadius: 10, background: "#f5f7f9", border: `1px dashed ${colors.border}`, fontSize: 12, color: colors.textSecondary, textAlign: "center" }}>
        No recovery loops yet. Add systems and crops to reveal circularity opportunities.
      </div>
    );
  }
  const totalPct = Math.round(circularityScore(bal, eff) * 100);
  // Discoverability: always surface Water & Energy rows. When there's no crop demand yet they show
  // as PENDING (greyed, NOT counted in the weighted total) so users see the affordance early.
  const present = new Set(rows.map((r) => r.key));
  const pending = [
    { key: "water", label: "Water loop", weight: eff.water, detail: "No crop water demand yet. Allocate crop area to activate." },
    { key: "energy", label: "Energy loop", weight: eff.energy, detail: "No energy demand yet. Allocate crop area to activate." },
  ].filter((p) => !present.has(p.key));
  const displayRows = [
    ...pending.filter((p) => p.key === "water"),
    ...rows.filter((r) => r.key === "water"),
    ...pending.filter((p) => p.key === "energy"),
    ...rows.filter((r) => r.key === "energy"),
    ...rows.filter((r) => r.key === "nutrient" || r.key === "carbon"),
  ];
  const existing = (systemBlocks || []).filter((b) => b.existing).length;
  const gaps = rows.filter((r) => r.status === "gap").length;
  return (
    <div data-testid="circularity-gaps">
      <div style={styles.circTotal} data-testid="circularity-total">
        <div style={styles.circTotalTop}>
          <span style={styles.circTotalLabel}>Resources recovered</span>
          <span style={styles.circTotalPct} data-testid="circularity-total-pct">{totalPct}%</span>
        </div>
        <div style={styles.gapBarTrack}><div style={{ ...styles.gapBarFill, width: `${Math.max(2, Math.min(100, totalPct))}%`, background: totalPct >= 100 ? MINT : RING_COLOR.resources }} /></div>
        <div style={styles.circTotalNote}>
          {circLockEqual ? "Equal weighting · locked (assumption-free)" : "Weighted average of the active loops"} · {gaps ? `${gaps} open loop${gaps === 1 ? "" : "s"}` : "all active loops closed"}
        </div>
        <button
          data-testid="circ-weights-cta"
          onClick={() => { if (circLockEqual) { setCircLockEqual(false); setShowWeights(true); } else { setShowWeights((v) => !v); } }}
          style={{ ...styles.circWeightsCta, ...(showWeights ? styles.circWeightsCtaOpen : {}) }}>
          <SlidersHorizontal size={13} />
          <span>{circLockEqual ? "Weights locked to Equal — tap to edit" : showWeights ? "Hide loop weights" : "Adjust loop weights"}</span>
          <span style={styles.circWeightsCtaVals}>
            {circLockEqual ? "25·25·25·25" : `${Math.round(circWeights.water * 100)}·${Math.round(circWeights.energy * 100)}·${Math.round(circWeights.nutrient * 100)}·${Math.round(circWeights.carbon * 100)}`}
          </span>
        </button>
        {!circLockEqual && showWeights && (
          <div style={styles.circWeightsBox} data-testid="circ-weights-editor">
            <div style={styles.circWeightsHead}>
              Loop weights <span style={styles.circWeightsAssume}>· how the 4 loops blend into the score above</span>
            </div>
            <div style={styles.circWeightsExplain}>
              These are a <strong>user assumption</strong> — there is no empirical “correct” split, so the default is <strong>equal (25% each)</strong>. Change them to reflect your site's priorities, pick a preset, or lock to equal for an assumption-free score. Each loop's share shows as “% of score” on its row below.
            </div>
            <div style={styles.circPresetRow}>
              {Object.entries(CIRC_PRESETS).map(([k, p]) => {
                const w = p.weights;
                const activePreset = ["water", "energy", "nutrient", "carbon"].every((x) => Math.round(circWeights[x] * 100) === Math.round(w[x] * 100));
                return (
                  <button key={k} data-testid={`circ-preset-${k}`} onClick={() => applyCircPreset(w)}
                    title={`${p.label}: W${Math.round(w.water * 100)} · E${Math.round(w.energy * 100)} · N${Math.round(w.nutrient * 100)} · C${Math.round(w.carbon * 100)}`}
                    style={{ ...styles.circPreset, ...(activePreset ? styles.circPresetActive : {}) }}>
                    {p.label}
                  </button>
                );
              })}
            </div>
            {[["water", "Water"], ["energy", "Energy"], ["nutrient", "Nutrient"], ["carbon", "Carbon"]].map(([k, lbl]) => (
              <div key={k} style={styles.circWeightRow}>
                <span style={styles.circWeightLbl}>{lbl}</span>
                <input type="number" min={0} max={100} step={5} data-testid={`circ-weight-${k}`}
                  value={Math.round((circWeights[k] || 0) * 100)}
                  onChange={(e) => setCircWeight(k, e.target.value)} style={styles.circWeightInput} />
                <span style={styles.circWeightPct}>%</span>
              </div>
            ))}
            {(() => {
              const sum = Math.round((circWeights.water + circWeights.energy + circWeights.nutrient + circWeights.carbon) * 100);
              return (
                <div style={styles.circWeightsFoot}>
                  <span style={{ color: sum === 100 ? colors.textSecondary : "#b06a0e" }} data-testid="circ-weights-sum">
                    Σ {sum}%{sum !== 100 ? " — renormalized to 100% in the score" : ""}
                  </span>
                  <span style={{ display: "flex", gap: 10 }}>
                    <button data-testid="circ-weights-reset" onClick={resetCircWeights} style={styles.circWeightsReset}>Reset to equal</button>
                    <button data-testid="circ-weights-lock" onClick={() => { setCircLockEqual(true); setShowWeights(false); }} style={styles.circWeightsReset}>Lock to equal</button>
                  </span>
                </div>
              );
            })()}
          </div>
        )}
        {(kpis.valorization_area_m2 > 0 || valorizationAreaCharged) && (
          <div style={styles.areaChargeBox} data-testid="area-charge-toggle-box">
            <button data-testid="area-charge-toggle" onClick={() => setValorizationAreaCharged(!valorizationAreaCharged)}
              style={{ ...styles.areaChargeToggle, ...(valorizationAreaCharged ? styles.areaChargeToggleOn : {}) }}>
              <span style={{ ...styles.areaChargeKnob, ...(valorizationAreaCharged ? styles.areaChargeKnobOn : {}) }} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={styles.areaChargeTitle}>Charge equipment area to growing area</div>
              <div style={styles.areaChargeNote} data-testid="area-charge-detail">
                {valorizationAreaCharged
                  ? `Valorization equipment ≈ ${Math.round(kpis.valorization_area_m2)} m² sited inside the grow envelope → growing area ${Math.round(kpis.grow_area_m2)} m² · displaces ~${Math.round(kpis.yield_displaced_kg)} kg/yr of crop yield.`
                  : `Valorization equipment ≈ ${Math.round(kpis.valorization_area_m2)} m² — currently informational (not deducted). Turn on to see the growing-area / yield trade-off.`}
              </div>
            </div>
          </div>
        )}
      </div>
      {existing > 0 && (
        <div style={styles.gapHint} data-testid="circularity-gaps-optnote">
          Optimization mode: existing measured systems and new drafts share resources; gaps below are net of the whole site.
        </div>
      )}
      {displayRows.map((r) => {
        const isPending = r.status === undefined;
        return (
          <div key={r.key} style={{ ...styles.gapRow, ...(isPending ? { opacity: 0.55 } : {}) }} data-testid={`gap-row-${r.key}`}>
            <div style={styles.gapRowTop}>
              <span style={styles.gapLabel}>
                {r.label} <span style={styles.gapWeight} data-testid={`gap-weight-${r.key}`} title="This loop's weight in the overall Resources Recovered score (all weights add up to 100%).">· {Math.round(r.weight * 100)}% of score</span>
                {!isPending && <span style={{ marginLeft: 6, display: "inline-flex", verticalAlign: "middle" }}>
                  <InfoTip testId={`gap-formula-${r.key}`} accent={colors.textTertiary} placement="top"
                    title={`${r.label} — how it's calculated`} body={r.key === "carbon"
                      ? `${LOOP_FORMULA.carbon} The badge reads NET-POSITIVE when the design avoids more CO₂ than it emits (net ≥ 0); the "${r.pct}% savings" is how much of the avoidable supply-chain carbon is saved — the two are different measures. The "${Math.round(r.weight * 100)}% of score" tag is carbon's weight in the overall average.`
                      : `${LOOP_FORMULA[r.key]} The "${Math.round(r.weight * 100)}% of score" tag is this loop's weight in the overall Resources Recovered average; the GAP/CLOSED badge is this loop's own closure.`} />
                </span>}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ ...styles.gapBadge, ...(isPending ? styles.gapBadgePending : r.status === "closed" ? styles.gapBadgeOk : styles.gapBadgeWarn) }} data-testid={`gap-status-${r.key}`}>
                  {isPending
                    ? "PENDING"
                    : r.key === "carbon"
                      ? `${r.status === "closed" ? "NET-POSITIVE" : "NET-NEGATIVE"} · ${r.pct}% savings`
                      : `${r.status === "closed" ? "CLOSED" : "GAP"} · ${r.pct}%`}
                </span>
                {!isPending && (
                  <button data-testid={`gap-detail-btn-${r.key}`} onClick={() => setDetailKey(r.key)}
                    title={`See full ${r.label} calculation`} aria-label={`See full ${r.label} calculation`}
                    style={styles.gapDetailBtn}>
                    <Search size={13} />
                  </button>
                )}
              </div>
            </div>
            {!isPending && <div style={styles.gapBarTrack}><div style={{ ...styles.gapBarFill, width: `${Math.max(2, Math.min(100, r.pct))}%`, background: r.status === "closed" ? MINT : "#E08A1E" }} /></div>}
            <div style={styles.gapDetail}>{r.detail}</div>
            {r.status === "gap" && <div style={styles.gapSuggest} data-testid={`gap-suggest-${r.key}`}>→ {GAP_SUGGEST[r.key]}</div>}
          </div>
        );
      })}
      {detailKey && (
        <CircularityDetailModal
          loopKey={detailKey}
          row={rows.find((r) => r.key === detailKey)}
          bal={bal}
          systems={(kb?.systems || []).filter((s) => selections.systems.includes(s.id)).map((s) => s.label)}
          crops={(kb?.crops || []).filter((c) => selections.crops.includes(c.id)).map((c) => c.cultivar || c.crop)}
          history={(loopHistory || []).map((e) => ({ systems: e.systems, pct: e[detailKey] })).filter((e) => e.pct != null)}
          onClose={() => setDetailKey(null)}
        />
      )}
    </div>
  );
}


export function RingGuideCard({ pillar, accent, ceiling }) {
  const g = RING_GUIDE[pillar];
  if (!g) return null;
  return (
    <div style={{ ...styles.guideCard, borderLeft: `3px solid ${accent}` }} data-testid={`ring-guide-${pillar}`}>
      <div style={{ ...styles.guideTitle, color: accent }}>{g.title}</div>
      <div style={styles.guideWhat}>{g.what}</div>
      <div style={styles.guideHow}><span style={styles.guideHowLabel}>Reach 100%: </span>{g.how}</div>
      {ceiling?.label && (
        <div style={styles.guideCeiling} data-testid={`ring-ceiling-${pillar}`}>
          <span style={styles.ceilDot} />
          <span style={styles.ceilText}>Site potential: <strong>{ceiling.label}</strong>{ceiling.detail ? ` · ${ceiling.detail}` : ""}</span>
        </div>
      )}
    </div>
  );
}

export function AdvancedKB({ kb }) {
  const setKb = useStudio((s) => s.setKb);
  const [status, setStatus] = React.useState("idle"); // idle | uploading | done | error
  const [msg, setMsg] = React.useState(null);
  const inputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setStatus("uploading");
    setMsg(file.name);
    try {
      const data = await kbApi.override(file);
      setKb(data);
      setStatus("done");
      setMsg(`Loaded ${data.counts?.crops ?? "?"} crops · ${data.counts?.resources ?? "?"} loops (source: ${data.source})`);
    } catch (e) {
      warn("kb override failed", e);
      setStatus("error");
      setMsg("Unrecognized workbook. Expected a crop or waste database (.xlsx).");
    }
  };

  const counts = kb?.counts || {};
  return (
    <div data-testid="advanced-kb-body">
      <div style={styles.kbStats}>
        <KbStat label="SOURCE" value={kb?.source || "—"} />
        <KbStat label="CROPS" value={counts.crops ?? "—"} />
        <KbStat label="SYSTEMS" value={counts.systems ?? "—"} />
        <KbStat label="LOOPS" value={counts.resources ?? "—"} />
      </div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer?.files?.[0]); }}
        style={{ ...styles.kbDrop, borderColor: status === "error" ? "#FF3B30" : status === "done" ? colors.ok : colors.borderStrong }}
        data-testid="kb-override-dropzone"
      >
        <span style={styles.kbDropIcon}>⤓</span>
        <span style={styles.kbDropText}>
          {status === "uploading" ? "Parsing workbook…" : "Override the baseline database (.xlsx workbook)"}
        </span>
        <input ref={inputRef} type="file" accept=".xlsx" style={{ display: "none" }}
          data-testid="kb-override-input"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </div>
      {msg && (
        <div style={{ ...styles.kbMsg, color: status === "error" ? "#FF3B30" : status === "done" ? colors.ok : colors.textTertiary }} data-testid="kb-override-status">
          {msg}
        </div>
      )}
    </div>
  );
}
// §10/§11 — Selected Surface: whole-face capture, utilized-area slider, footprint placement
export function ScenarioFacePanel({ kb }) {
  const scenarioFaces = useStudio((s) => s.scenarioFaces);
  const activeFaceId = useStudio((s) => s.activeFaceId);
  const updateScenarioFace = useStudio((s) => s.updateScenarioFace);
  const removeScenarioFace = useStudio((s) => s.removeScenarioFace);
  const setActiveFace = useStudio((s) => s.setActiveFace);
  const requestPlacement = useStudio((s) => s.requestPlacement);
  const selections = useStudio((s) => s.selections);
  const lastSystemHost = useStudio((s) => s.lastSystemHost);
  const setLastSystemHost = useStudio((s) => s.setLastSystemHost);
  // Location captures render nested under their location option (whole-face "Available Canvas");
  // this panel drives the Systems sub-area drafting / placement flow only.
  const faces = scenarioFaces.filter((f) => f.pillar !== "locations");
  if (!faces.length || !kb) return null;
  const active = faces.find((f) => f.id === activeFaceId) || faces[0];
  const pc = RING_COLOR[active.pillar] || RING_COLOR.systems;
  const sysOptions = sortSystemsByHost(selections.systems.length ? kb.systems.filter((s) => selections.systems.includes(s.id)) : kb.systems);
  const chosenSys = active.systemId || sysOptions[0]?.id || "";
  const util = active.utilizedArea ?? active.totalArea;
  const pct = active.totalArea ? Math.round((util / active.totalArea) * 100) : 0;
  return (
    <section style={styles.section} data-testid="scenario-face-panel">
      <div style={styles.sectionHead}>
        <span style={{ ...styles.pillarTag, background: pc }}>SELECTED SURFACE</span>
        <span style={styles.multiHint}>{faces.length} face{faces.length > 1 ? "s" : ""} in scenario</span>
      </div>
      {faces.length > 1 && (
        <div style={styles.faceChips}>
          {faces.map((f) => (
            <button key={f.id} data-testid={`scenario-face-chip-${f.id}`} onClick={() => setActiveFace(f.id)}
              style={{ ...styles.faceChip, ...(f.id === active.id ? { borderColor: pc, color: pc } : {}) }}>{f.tag}{f.extruded ? " ✓" : ""}</button>
          ))}
        </div>
      )}
      <div style={styles.faceCard}>
        <div style={styles.faceTopRow}>
          <span style={styles.faceLabel}>{active.tag}</span>
          {active.extruded && <span style={{ ...styles.faceDone, color: pc, borderColor: pc }}>PLACED</span>}
        </div>
        <div style={styles.faceAssignRow}>
          {["locations", "systems", "crops", "resources"].map((p) => (
            <button key={p} data-testid={`face-assign-${p}`} title={`Assign to ${p} loop`}
              onClick={() => updateScenarioFace(active.id, { pillar: p })}
              style={{ ...styles.assignChip, ...(active.pillar === p ? { background: RING_COLOR[p], color: "#fff", borderColor: "transparent" } : { color: RING_COLOR[p] }) }}>
              {(PILLAR_BY_ID[p]?.label || p)}
            </button>
          ))}
        </div>
        <div style={styles.faceMetric}>
          <span style={styles.faceMetricVal} data-testid="face-total-area">{active.totalArea.toLocaleString()}</span>
          <span style={styles.faceMetricUnit}>m² total available area</span>
        </div>
        {!active.extruded && (
          <>
            <div style={styles.faceSliderHead}>
              <span>Utilized BIA Area</span>
              <span data-testid="face-utilized-area" style={{ color: pc, fontWeight: 700 }}>{Math.round(util)} m² · {pct}%</span>
            </div>
            <input type="range" min={1} max={Math.max(1, Math.round(active.totalArea))} value={Math.round(util)}
              data-testid="face-utilized-slider" style={{ ...styles.faceSlider, accentColor: pc }}
              onChange={(e) => updateScenarioFace(active.id, { utilizedArea: Number(e.target.value) })} />
            <div style={styles.faceHint}>Drag the highlighted footprint on the model to position it. It stays flush to the surface and is constrained to the face edges.</div>
            <div style={styles.faceActionRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SystemSelect testId="face-system-select" value={chosenSys} options={sysOptions}
                  lastHost={lastSystemHost}
                  onChange={(id) => updateScenarioFace(active.id, { systemId: id })}
                  onPickHost={(h) => setLastSystemHost(h)} />
              </div>
              <button data-testid="face-confirm-placement" disabled={!chosenSys}
                onClick={() => requestPlacement(active.id, chosenSys)}
                style={{ ...styles.confirmBtn, background: pc, opacity: chosenSys ? 1 : 0.5 }}>Confirm Placement</button>
            </div>
          </>
        )}
        <button data-testid="face-release" onClick={() => removeScenarioFace(active.id)} style={styles.releaseBtn}>Release surface</button>
      </div>
    </section>
  );
}
// Small, dismissible banner naming the v5.1 systems held back as data-pending (excluded from calcs).
function PendingDataBanner({ systems }) {
  const [dismissed, setDismissed] = React.useState(false);
  const pending = (systems || []).filter((s) => s.data_pending || s.status === "PENDING");
  if (dismissed || pending.length === 0) return null;
  const names = pending.map((s) => systemDisplayLabel(s)).join(" · ");
  return (
    <div data-testid="pending-data-banner" style={{
      display: "flex", alignItems: "flex-start", gap: 8, margin: "0 0 8px", padding: "8px 10px",
      borderRadius: 9, background: "#8a8a8f14", border: "1px solid #8a8a8f33",
    }}>
      <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase",
        color: "#6e6e73", background: "#8a8a8f22", borderRadius: 999, padding: "2px 7px", marginTop: 1 }}>Data pending</span>
      <span style={{ flex: 1, fontSize: 11, lineHeight: 1.4, color: colors.textSecondary }}>
        {pending.length} v5.1 {pending.length === 1 ? "system is" : "systems are"} awaiting vetted data and are <strong>excluded from calculations</strong>: {names}.
      </span>
      <button onClick={() => setDismissed(true)} data-testid="pending-data-banner-dismiss" aria-label="Dismiss"
        style={{ flexShrink: 0, border: "none", background: "transparent", color: colors.textTertiary, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}


export function PillarOptions({ kb, rootPillar, relTags = { locations: {}, systems: {}, crops: {}, resources: {} }, selections, toggle, weather, areaFocusSystem, setAreaFocus, allocDraw, setAllocDraw, grossAreas, cropDraft, beginCropDraft, setCropDraftSystem, clearCropDraft, commitCropDraft, pickTool, setPickTool, modelReady, systemBlocks = [], activeBlockId, setActiveBlock, removeSystemBlock, addBlockCrop, removeBlockCrop, setBlockAllocation, resetBlockShares, pairDraft, beginPair, setPairField, clearPairDraft, commitPair, pausedDrafts = [], pauseDraft, resumeDraft, removePausedDraft, ripple }) {
  const pairProps = { kb, draft: pairDraft, modelReady, setPairField, commitPair, clearPairDraft, pauseDraft, setPickTool, setAreaFocus, areaFocusSystem, allocDraw, setAllocDraw, grossAreas, pickTool, ripple };
  const instanceProps = { kb, blocks: systemBlocks, activeBlockId, onSelect: setActiveBlock, onRemove: removeSystemBlock, onAddCrop: addBlockCrop, onRemoveCrop: removeBlockCrop, onShare: setBlockAllocation, onReset: resetBlockShares };
  // Draft-session guard (Phase 2): starting a NEW draft while one is active routes through the
  // request* actions, which prompt "Close active draft" vs "Keep editing" instead of silently
  // stacking drafts. Toggling OFF the currently-anchored option still clears directly (no prompt).
  const requestBeginPair = useStudio((s) => s.requestBeginPair);
  const requestBeginCropDraft = useStudio((s) => s.requestBeginCropDraft);
  const pendingDraftAction = useStudio((s) => s.pendingDraftAction);
  const keepEditingDraft = useStudio((s) => s.keepEditingDraft);
  const closeActiveAndProceed = useStudio((s) => s.closeActiveAndProceed);
  const draftPrompt = pendingDraftAction
    ? <DraftPrompt kb={kb} pending={pendingDraftAction} onKeep={keepEditingDraft} onClose={closeActiveAndProceed} />
    : null;
  const pausedRow = (pausedDrafts.length > 0 && (rootPillar === "systems" || rootPillar === "locations")) ? (
    <PausedDrafts kb={kb} drafts={pausedDrafts} onResume={resumeDraft} onRemove={removePausedDraft} />
  ) : null;

  if (rootPillar === "locations") {
    return (
      <>
        {draftPrompt}
        <PairedInstances {...instanceProps} />
        {pausedRow}
        <div style={styles.groupHead}>ADD AN ALLOCATION ZONE</div>
        {kb.locations.map((l) => {
          const anchored = pairDraft?.origin === "locations" && pairDraft.locationId === l.id;
          const rt = relTags.locations[l.id] || {};
          return (
            <div key={l.id}>
              <OptionRow accent={PILLAR_BY_ID.locations.solid} active={anchored}
                title={l.label} desc={l.short} testID={`opt-location-${l.id}`}
                onClick={() => (anchored ? clearPairDraft() : requestBeginPair("locations", "locationId", l.id))}
                icon={locationIcon(l)} tip={getOptTip("locations", l)}
                recommended={rt.recommended} dim={rt.dim} recReason={rt.reason} />
              {anchored && <PairPanel {...pairProps} />}
            </div>
          );
        })}
      </>
    );
  }

  if (rootPillar === "systems") {
    const items = systemsFor(kb, selections.locations);
    const groups = {};
    items.forEach((s) => { (groups[s.group] = groups[s.group] || []).push(s); });
    // Context = committed paired locations + the in-progress pairDraft location, so ground vs
    // rooftop/facade filtering reacts the moment a Location is chosen (not only on commit).
    const ctxLoc = [...new Set([...(selections.locations || []), ...(pairDraft?.locationId ? [pairDraft.locationId] : [])])];
    return (
      <>
        {draftPrompt}
        <PairedInstances {...instanceProps} />
        {pausedRow}
        <div style={styles.groupHead}>ADD A SYSTEM</div>
        <PendingDataBanner systems={kb.systems} />
        {(selections.locations.length > 0 || pairDraft?.locationId) && (
          <Hint>Systems compatible with the selected zone are highlighted; ground-only systems are unavailable on rooftops and façades.</Hint>
        )}
        {Object.keys(groups).sort((a, b) => {
          const order = ["Proprietary Systems", "Hydroponic & Aquaponic", "Soil & Substrate Beds",
            "Green Roofs", "Vertical Greening (Facade)", "Aquatic & Algae"];
          const ia = order.indexOf(a), ib = order.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        }).map((g) => (
          <div key={g} style={{ marginBottom: 6 }}>
            <div style={styles.groupHead}>{g}</div>
            {sortSystemsByHost(groups[g]).map((s) => {
              const anchored = pairDraft?.origin === "systems" && pairDraft.systemId === s.id;
              const rt = relTags.systems[s.id] || {};
              const fitsCtx = ctxLoc.length ? (s.locations || []).some((l) => ctxLoc.includes(l)) : null;
              const hardExcluded = s.ground_contact && ctxLoc.length > 0 && fitsCtx === false;
              // Anchor & Ripple (Scenario A / location-anchored): a fixed-footprint system that
              // no longer fits the remaining m² wallet is rippled out.
              const overWallet = ripple?.anchorRing === "locations" && ripple.walletArea > 0 && systemMinFootprint(s) > ripple.walletArea;
              const excluded = rt.excluded || hardExcluded || overWallet;
              const reason = overWallet
                ? `Needs ${systemMinFootprint(s)} m². Only ${Math.round(ripple.walletArea).toLocaleString()} m² left in your area wallet`
                : hardExcluded ? "Requires on-grade ground contact; not available at the selected rooftop / facade" : rt.reason;
              const recommended = !excluded && (rt.recommended || fitsCtx === true);
              const pending = s.data_pending || s.status === "PENDING";
              return (
                <div key={s.id}>
                  <OptionRow accent={PILLAR_BY_ID.systems.solid} active={anchored} title={systemDisplayLabel(s)}
                    desc={s.specs?.footprint_m2 ? `Footprint ${s.specs.footprint_m2} · ${s.crop_count} crops` : `${s.crop_count} compatible crops`}
                    tags={s.tags} testID={`opt-system-${s.id}`}
                    onClick={() => (anchored ? clearPairDraft() : requestBeginPair("systems", "systemId", s.id))}
                    icon={systemIcon(s)} tip={getOptTip("systems", s)} featured={s.proprietary}
                    recommended={recommended} dim={rt.dim} pending={pending}
                    excluded={excluded} recReason={reason} />
                  {anchored && <PairPanel {...pairProps} />}
                </div>
              );
            })}
          </div>
        ))}
      </>
    );
  }

  if (rootPillar === "crops") {
    const groups = cropGroups(kb, selections.systems);
    const dli = weather?.dli;
    const groupNames = Object.keys(groups);
    return (
      <>
        {draftPrompt}
        <PairedInstances {...instanceProps} />
        {selections.systems.length === 0
          ? <Hint>Select a crop to begin.</Hint>
          : <Hint>Crops your systems can grow are marked Recommended.</Hint>}
        {groupNames.map((g) => (
          <div key={g} style={{ marginBottom: 6 }}>
            <div style={styles.groupHead}>{g}</div>
            {groups[g].map((c) => {
              const inRange = !dli || c.dli_min == null || (dli >= c.dli_min && dli <= c.dli_max);
              const anchored = cropDraft?.cropId === c.id;
              const dliTxt = c.dli_min != null && c.dli_max != null ? `DLI ${c.dli_min}–${c.dli_max}` : null;
              const cropDesc = [c.crop, dliTxt, c.market_value ? `$${c.market_value}/kg` : null].filter(Boolean).join(" · ");
              return (
                <div key={c.id}>
                  <OptionRow accent={PILLAR_BY_ID.crops.solid} active={anchored || selections.crops.includes(c.id)}
                    title={c.cultivar} desc={cropDesc}
                    tags={c.tags} statusTag={dli ? (inRange ? "match" : "suboptimal") : null} statusOk={inRange}
                    testID={`opt-crop-${c.id}`} onClick={() => (anchored ? clearCropDraft() : requestBeginCropDraft(c.id))}
                    icon={cropIcon(c)} tip={getOptTip("crops", c)}
                    recommended={relTags.crops[c.id]?.recommended} recReason={relTags.crops[c.id]?.reason} />
                  {anchored && <CropPairPanel kb={kb} crop={c} draft={cropDraft} setSystem={setCropDraftSystem} commit={commitCropDraft} clear={clearCropDraft} />}
                </div>
              );
            })}
          </div>
        ))}
      </>
    );
  }

  // resources — Ring-specific rule: straight [Pick from List] + parametric numeric sizing
  // (no 3D placement / hybrid prompt for Crops & Resources).
  const { inputs, outputs } = resourcesFor(kb);
  const allRes = [...inputs, ...outputs];
  const RES_GROUPS = ["Organic Valorization", "Water Recovery & Treatment", "Renewable Energy", "Thermal Exchange", "Carbon (CO2) Recovery"];
  const RES_GROUP_LABEL = { "Organic Valorization": "Organics", "Water Recovery & Treatment": "Water Recovery", "Renewable Energy": "Renewables", "Thermal Exchange": "Thermal", "Carbon (CO2) Recovery": "Carbon (CO₂) Recovery" };
  const grouped = {};
  allRes.forEach((r) => { const g = resourceGroup(r); (grouped[g] = grouped[g] || []).push(r); });
  const renderRes = (r) => (
    <ResourceOption key={r.id} r={r} active={selections.resources.includes(r.id)} toggle={toggle} relTags={relTags} />
  );
  return (
    <>
      {draftPrompt}
      {RES_GROUPS.filter((g) => grouped[g]?.length).map((g) => (
        <div key={g} style={{ marginBottom: 6 }} data-testid={`resource-group-${g.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
          <div style={{ ...styles.groupHead, marginTop: 8 }}>{RES_GROUP_LABEL[g] || g}</div>
          {grouped[g].map(renderRes)}
        </div>
      ))}
      <div style={{ marginTop: 10 }}>
        <ReferencesButton citations={kb?.citations} title="References & Data Sources" label="View all references" testId="references-button-resources" />
      </div>
    </>
  );
}
